import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, GripVertical, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Step {
  id?: string;
  title: string;
  instruction_rich_text: string;
  step_type: string;
  required_proof_type: string;
  sla_minutes: number | null;
  is_critical: boolean;
  order_index: number;
}

interface SOPEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  sopId?: string;
  onSuccess: () => void;
}

export function SOPEditorDialog({
  open,
  onOpenChange,
  organizationId,
  sopId,
  onSuccess,
}: SOPEditorDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState<number | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (open && sopId) {
      fetchSOP();
    } else if (open && !sopId) {
      resetForm();
    }
  }, [open, sopId]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setTags([]);
    setTagInput("");
    setEstimatedDuration(null);
    setSteps([]);
  };

  const fetchSOP = async () => {
    if (!sopId) return;
    setLoading(true);
    try {
      const { data: sop, error: sopError } = await supabase
        .from("sop_templates")
        .select("*")
        .eq("id", sopId)
        .single();

      if (sopError) throw sopError;

      setTitle(sop.title);
      setDescription(sop.description || "");
      setTags(sop.tags || []);
      setEstimatedDuration(sop.estimated_duration_minutes);

      const { data: stepsData, error: stepsError } = await supabase
        .from("sop_steps")
        .select("*")
        .eq("sop_template_id", sopId)
        .order("order_index", { ascending: true });

      if (stepsError) throw stepsError;

      setSteps(
        stepsData.map((s) => ({
          id: s.id,
          title: s.title,
          instruction_rich_text: s.instruction_rich_text || "",
          step_type: s.step_type,
          required_proof_type: s.required_proof_type,
          sla_minutes: s.sla_minutes,
          is_critical: s.is_critical,
          order_index: s.order_index,
        }))
      );
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const addStep = () => {
    setSteps([
      ...steps,
      {
        title: "",
        instruction_rich_text: "",
        step_type: "TASK",
        required_proof_type: "NONE",
        sla_minutes: null,
        is_critical: false,
        order_index: steps.length,
      },
    ]);
  };

  const updateStep = (index: number, updates: Partial<Step>) => {
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order_index: i })));
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "SOP title is required",
      });
      return;
    }

    setSaving(true);
    try {
      let templateId = sopId;

      if (sopId) {
        const { error } = await supabase
          .from("sop_templates")
          .update({
            title,
            description,
            tags,
            estimated_duration_minutes: estimatedDuration,
          })
          .eq("id", sopId);

        if (error) throw error;

        // Delete existing steps and recreate
        await supabase.from("sop_steps").delete().eq("sop_template_id", sopId);
      } else {
        const { data: newSop, error } = await supabase
          .from("sop_templates")
          .insert({
            title,
            description,
            tags,
            estimated_duration_minutes: estimatedDuration,
            organization_id: organizationId,
            status: "DRAFT",
          })
          .select()
          .single();

        if (error) throw error;
        templateId = newSop.id;
      }

      // Insert steps
      if (steps.length > 0 && templateId) {
        const { error: stepsError } = await supabase.from("sop_steps").insert(
          steps.map((step, index) => ({
            sop_template_id: templateId,
            title: step.title,
            instruction_rich_text: step.instruction_rich_text,
            step_type: step.step_type as "TASK" | "APPROVAL" | "DECISION" | "INTEGRATION" | "WAIT",
            required_proof_type: step.required_proof_type as "NONE" | "TEXT_NOTE" | "FILE_UPLOAD" | "PHOTO_UPLOAD" | "FORM",
            sla_minutes: step.sla_minutes,
            is_critical: step.is_critical,
            order_index: index,
          }))
        );

        if (stepsError) throw stepsError;
      }

      toast({
        title: sopId ? "SOP Updated" : "SOP Created",
        description: sopId
          ? "Your SOP has been updated successfully."
          : "Your SOP has been created successfully.",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-lg sm:text-xl">{sopId ? "Edit SOP" : "Create SOP"}</DialogTitle>
          <DialogDescription className="text-sm">
            {sopId
              ? "Update your standard operating procedure"
              : "Create a new standard operating procedure with steps"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6 py-2 sm:py-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter SOP title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose and scope of this SOP"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add tag"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={addTag} size="sm">
                    Add
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="cursor-pointer text-xs"
                        onClick={() => removeTag(tag)}
                      >
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration" className="text-sm">Estimated Duration (minutes)</Label>
                <Input
                  id="duration"
                  type="number"
                  value={estimatedDuration || ""}
                  onChange={(e) =>
                    setEstimatedDuration(e.target.value ? parseInt(e.target.value) : null)
                  }
                  placeholder="e.g., 30"
                />
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Steps</Label>
                <Button type="button" variant="outline" size="sm" onClick={addStep}>
                  <Plus className="h-4 w-4 mr-1" />
                  <span className="hidden xs:inline">Add Step</span>
                  <span className="xs:hidden">Add</span>
                </Button>
              </div>

              {steps.length === 0 ? (
                <Card>
                  <CardContent className="py-6 sm:py-8 text-center text-muted-foreground text-sm">
                    No steps yet. Click "Add Step" to get started.
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {steps.map((step, index) => (
                    <Card key={index} className="border-l-4 border-l-primary">
                      <CardContent className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                        <div className="flex items-start gap-2">
                          <GripVertical className="h-5 w-5 text-muted-foreground mt-1 cursor-grab hidden sm:block" />
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-2">
                              <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                                Step {index + 1}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 ml-auto text-destructive hover:text-destructive"
                                onClick={() => removeStep(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            <Input
                              value={step.title}
                              onChange={(e) => updateStep(index, { title: e.target.value })}
                              placeholder="Step title"
                              className="text-sm"
                            />
                            <Textarea
                              value={step.instruction_rich_text}
                              onChange={(e) =>
                                updateStep(index, { instruction_rich_text: e.target.value })
                              }
                              placeholder="Instructions for this step"
                              rows={2}
                              className="text-sm"
                            />
                            <div className="grid grid-cols-1 xs:grid-cols-3 gap-2 sm:gap-3">
                              <Select
                                value={step.step_type}
                                onValueChange={(value) => updateStep(index, { step_type: value })}
                              >
                                <SelectTrigger className="text-xs sm:text-sm">
                                  <SelectValue placeholder="Type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="TASK">Task</SelectItem>
                                  <SelectItem value="APPROVAL">Approval</SelectItem>
                                  <SelectItem value="REVIEW">Review</SelectItem>
                                  <SelectItem value="NOTIFICATION">Notification</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select
                                value={step.required_proof_type}
                                onValueChange={(value) =>
                                  updateStep(index, { required_proof_type: value })
                                }
                              >
                                <SelectTrigger className="text-xs sm:text-sm">
                                  <SelectValue placeholder="Proof" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="NONE">No Proof</SelectItem>
                                  <SelectItem value="TEXT">Text Note</SelectItem>
                                  <SelectItem value="FILE">File Upload</SelectItem>
                                  <SelectItem value="CHECKBOX">Checkbox</SelectItem>
                                </SelectContent>
                              </Select>
                              <Input
                                type="number"
                                value={step.sla_minutes || ""}
                                onChange={(e) =>
                                  updateStep(index, {
                                    sla_minutes: e.target.value ? parseInt(e.target.value) : null,
                                  })
                                }
                                placeholder="SLA (min)"
                                className="text-xs sm:text-sm"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={step.is_critical}
                                onCheckedChange={(checked) =>
                                  updateStep(index, { is_critical: checked })
                                }
                              />
                              <Label className="text-xs sm:text-sm">Critical Step</Label>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {sopId ? "Save Changes" : "Create SOP"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
